import type { SupportedLanguage } from './index'

/**
 * Define localized routes here.
 * Each key maps to a route name, with translations for each language.
 *
 * Note: there is intentionally NO `contacts` route — phone, email, address,
 * and the chat CTA live in the footer's first column on every screen.
 */
export const localizedRoutes = {
  // Top-level
  home: { it: '', en: '' },
  company: { it: 'azienda', en: 'company' },
  howWeWork: { it: 'come-lavoriamo', en: 'how-we-work' },

  // Products
  products: { it: 'prodotti', en: 'products' },
  productsColorTechnical: {
    it: 'prodotti/colore-e-tecnici',
    en: 'products/color-and-technical',
  },
  productsScalpHairCare: {
    it: 'prodotti/scalp-hair-care',
    en: 'products/scalp-hair-care',
  },
  productsStyling: { it: 'prodotti/styling', en: 'products/styling' },

  // Insights
  insights: { it: 'approfondimenti', en: 'insights' },
  insightsPrivateLabelHairCare: {
    it: 'approfondimenti/private-label-hair-care',
    en: 'insights/private-label-hair-care',
  },
  insightsPrivateLabelCosmeticCompany: {
    it: 'approfondimenti/azienda-private-label-cosmetics',
    en: 'insights/private-label-cosmetic-company',
  },
  insightsPrivateLabelCosmetics: {
    it: 'approfondimenti/private-label-cosmetics',
    en: 'insights/private-label-cosmetics',
  },
  insightsPrivateLabelShampoo: {
    it: 'approfondimenti/produzione-shampoo-private-label',
    en: 'insights/private-label-shampoo-manufacturing',
  },
  insightsLuxuryPrivateLabel: {
    it: 'approfondimenti/cosmetici-luxury-private-label',
    en: 'insights/luxury-private-label-cosmetics',
  },
  insightsHairCareManufacturers: {
    it: 'approfondimenti/produttori-hair-care',
    en: 'insights/hair-care-manufacturers',
  },
  insightsCosmeticsManufacturers: {
    it: 'approfondimenti/produttori-cosmetici',
    en: 'insights/cosmetics-manufacturers',
  },

  // Editorial / Compliance / Sustainability
  magazine: { it: 'magazine', en: 'magazine' },
  mocra: { it: 'mocra-compliance', en: 'mocra-compliance' },
  qualityPolicy: { it: 'quality-policy', en: 'quality-policy' },
  sustainability: {
    it: 'sostenibilita-ed-etica',
    en: 'sustainability-and-ethic',
  },

  // Careers / Suppliers
  workWithUs: { it: 'lavora-con-noi', en: 'work-with-us' },
  becameSupplier: { it: 'diventa-fornitore', en: 'became-a-supplier' },

  // Legal
  privacy: { it: 'privacy-policy', en: 'privacy-policy' },
  cookie: { it: 'cookie-policy', en: 'cookie-policy' },
} as const

export type RouteKey = keyof typeof localizedRoutes

/**
 * Get the localized path for a route
 */
export function getLocalizedPath(
  routeKey: RouteKey,
  lang: SupportedLanguage
): string {
  const path = localizedRoutes[routeKey][lang]
  return path ? `/${lang}/${path}` : `/${lang}`
}

/**
 * Find route key from a path segment (or nested path).
 * Compares against the full slug (which may contain `/`).
 */
export function findRouteKeyFromPath(
  pathSegment: string,
  lang: SupportedLanguage
): RouteKey | null {
  for (const [key, routes] of Object.entries(localizedRoutes)) {
    if (routes[lang] === pathSegment) {
      return key as RouteKey
    }
  }
  return null
}
